// Purpose: shell for unauthenticated external flows (recipient-side
// share landing + download, and external dropbox uploads). Full-bleed
// composition to match the sign-in hero: atmospheric background stack,
// top nav with logo + status pill, compliance footer, centered main
// column.
//
// Intentionally does NOT show the theme toggle per Phase 7.5 scope
// (external users don't get theme choice; first-paint matches their OS
// via the boot script). The chrome matches the sign-in language so an
// external recipient immediately recognizes the same brand.

import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import {
  GridPattern,
  OrbitalMesh,
  PadlockWatermark,
  RadialGradientBg,
} from '../components/atmosphere/index.ts';
import { ComplianceBanner } from '../components/ComplianceBanner.tsx';
import { NoticeLink } from '../components/legal/NoticeLink.tsx';
import { AppHeader } from '../components/nav/AppHeader.tsx';
import { StatusPill } from '../components/ui/StatusPill.tsx';

const APP_VERSION = (import.meta.env.VITE_AST_API_VERSION as string | undefined) ?? 'dev';

export interface ExternalShellProps {
  children?: ReactNode;
}

export function ExternalShell({ children }: ExternalShellProps) {
  return (
    <div className="relative min-h-screen w-screen overflow-x-hidden bg-bg-primary text-fg-primary">
      <div aria-hidden className="absolute inset-0 z-0 pointer-events-none">
        <RadialGradientBg />
        <GridPattern />
        <PadlockWatermark />
        <OrbitalMesh />
      </div>
      <AppHeader
        logoOnly
        rightSlot={<StatusPill tone="success">All Systems Operational</StatusPill>}
      />
      <main className="relative z-10 pt-32 pb-24 px-8">
        <div className="mx-auto max-w-4xl">{children ?? <Outlet />}</div>
        <div className="mx-auto max-w-4xl mt-16 flex justify-center">
          <NoticeLink />
        </div>
      </main>
      <ComplianceBanner version={`v${APP_VERSION}`} />
    </div>
  );
}
